import { Capacitor } from "@capacitor/core";
import { KeepAwake } from "@capacitor-community/keep-awake";
import { App } from "@capacitor/app";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Haptics, NotificationType } from "@capacitor/haptics";
import { Share } from "@capacitor/share";
import { SplashScreen } from "@capacitor/splash-screen";
import {
  base64ToBytes,
  bytesToBase64,
  type DeclaredAgeRange,
  type NetworkStatus,
  type PlatformAdapter,
  type PlatformCapability,
  type PlatformType,
  type PushPayload,
  type ShareContent,
  type ShareImage,
  type ShareImageOutcome,
  type ShareOutcome,
} from "./types";
import { isPhotosSaveAvailable, savePhoto } from "./photos";
import { createAudioSessionArbiter } from "./soundSession";

export class CapacitorPlatformAdapter implements PlatformAdapter {
  private micStream: MediaStream | null = null;

  async queryMicPermission(): Promise<PermissionState> {
    // iOS's own record permission is the truth: Capacitor grants every WebKit
    // capture request itself, so `navigator.permissions` cannot see it. This
    // never prompts. The previous body called getUserMedia here, so the
    // system alert fired before the context screen (R9.4, task 11.4).
    const { queryNativeMicPermission } = await import("./audioSession");
    const native = await queryNativeMicPermission(Capacitor.isNativePlatform());
    if (native) return native;
    try {
      const result = await navigator.permissions.query({ name: "microphone" as PermissionName });
      return result.state;
    } catch {
      return "prompt";
    }
  }

  async getMicStream(): Promise<MediaStream> {
    this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    return this.micStream;
  }

  releaseMicStream(): void {
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
  }

  // R7.1: app-private files under Library/NoCloud (never in iCloud backup), not
  // `localStorage`, which WKWebView may evict under storage pressure. One file per key, base64 on the bridge.
  // Reads fall back to the pre-Filesystem `localStorage` copy once and move it.
  async saveLocal(key: string, data: Uint8Array): Promise<void> {
    await Filesystem.writeFile({
      path: localPath(key),
      data: bytesToBase64(data),
      directory: Directory.LibraryNoCloud,
      recursive: true,
    });
  }

  async loadLocal(key: string): Promise<Uint8Array | null> {
    try {
      const { data } = await Filesystem.readFile({ path: localPath(key), directory: Directory.LibraryNoCloud });
      if (typeof data === "string") return base64ToBytes(data);
      return new Uint8Array(await data.arrayBuffer());
    } catch {
      return this.migrateLegacy(key);
    }
  }

  async deleteLocal(key: string): Promise<void> {
    try { localStorage.removeItem(key); } catch { /* no legacy copy */ }
    try {
      await Filesystem.deleteFile({ path: localPath(key), directory: Directory.LibraryNoCloud });
    } catch { /* already absent */ }
  }

  private async migrateLegacy(key: string): Promise<Uint8Array | null> {
    let raw: string | null = null;
    try { raw = localStorage.getItem(key); } catch { return null; }
    if (!raw) return null;
    const bytes = base64ToBytes(raw);
    try {
      await this.saveLocal(key, bytes);
      localStorage.removeItem(key);
    } catch { /* keep the legacy copy; it still loads */ }
    return bytes;
  }

  async registerPushToken(): Promise<string> {
    // In production, use @capacitor/push-notifications plugin
    return "";
  }

  onPushReceived(_handler: (payload: PushPayload) => void): void {
    // In production, wire to @capacitor/push-notifications listener
  }

  getPlatform(): PlatformType {
    const p = Capacitor.getPlatform();
    if (p === "android") return "android";
    if (p === "ios") return "ios";
    return "web";
  }

  getScreenSize() {
    return { width: window.innerWidth, height: window.innerHeight };
  }

  isCapabilityAvailable(cap: PlatformCapability): boolean {
    switch (cap) {
      case "microphone": return true; // Native always has mic
      case "push-notifications": return true;
      case "file-storage": return true;
      // R7.3: no shipping feature uses the camera. Claiming it invites an
      // `NSCameraUsageDescription` we cannot justify, and an unexplained
      // permission is a rejection risk for a kids app. Flip this on with the
      // feature that needs it, not before.
      case "camera": return false;
    }
  }

  isOnline(): boolean {
    // `@capacitor/network` would give the richer connection type, but
    // `navigator.onLine` is accurate inside WKWebView for the reachable/not
    // question and needs no plugin. Upgrade if we ever need cellular-vs-wifi.
    return typeof navigator === "undefined" ? true : navigator.onLine !== false;
  }

  onNetworkChange(handler: (status: NetworkStatus) => void): () => void {
    if (typeof window === "undefined") return () => {};
    const online = () => handler({ online: true });
    const offline = () => handler({ online: false });
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }

  async activateVoiceAudio(): Promise<void> {
    // R3.3 — speaker not earpiece, audible through the mute switch, echo
    // cancellation on. No-ops off-native and never throws: a failed audio
    // config must not abort session start.
    await nativeAudioSession.activateVoice();
  }

  async deactivateVoiceAudio(): Promise<void> {
    await nativeAudioSession.deactivateVoice();
  }

  async requestSoundPlayback(): Promise<void> {
    await nativeAudioSession.requestSound();
  }

  async releaseSoundPlayback(): Promise<void> {
    await nativeAudioSession.releaseSound();
  }

  // R16.1. The plugin rejects with "Share canceled" when the person closes the
  // sheet; that is a normal outcome, not a reason to fall back to copy-link.
  async share(content: ShareContent): Promise<ShareOutcome> {
    try {
      if (!(await Share.canShare()).value) return "unavailable";
    } catch {
      return "unavailable";
    }
    try {
      await Share.share({ title: content.title, text: content.text, url: content.url });
      return "shared";
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return /cancel/i.test(message) ? "cancelled" : "unavailable";
    }
  }

  canSaveToPhotos(): boolean {
    return isPhotosSaveAvailable();
  }

  // game-on-freeform U18: straight to Photos where the shell has WBMPhotos. A refused
  // permission reads as cancelled, so the kid sees no error for a grown-up's choice.
  // game-on-freeform U7, for older shells: the share sheet takes a file URI, so the picture
  // goes to the cache first (never iCloud-backed, cleared by the system); "Save Image" is in the sheet.
  async shareImage(image: ShareImage, _prefer: "share" | "download"): Promise<ShareImageOutcome> {
    if (this.canSaveToPhotos()) {
      try {
        return (await savePhoto(image.base64)) === "saved" ? "saved" : "cancelled";
      } catch {
        return "unavailable";
      }
    }
    let uri: string;
    try {
      const path = `wbm-share/${image.name}`;
      await Filesystem.writeFile({ path, data: image.base64, directory: Directory.Cache, recursive: true });
      uri = (await Filesystem.getUri({ path, directory: Directory.Cache })).uri;
    } catch {
      return "unavailable";
    }
    try {
      await Share.share({ files: [uri] });
      return "shared";
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return /cancel/i.test(message) ? "cancelled" : "unavailable";
    }
  }

  // R16.2. Universal Links arrive as full https URLs; the router wants a path.
  onAppUrlOpen(handler: (path: string) => void): () => void {
    let removed = false;
    let remove: (() => Promise<void>) | null = null;
    App.addListener("appUrlOpen", ({ url }) => {
      const path = inAppPath(url);
      if (path) handler(path);
    })
      .then((h) => {
        remove = h.remove;
        if (removed) void remove();
      })
      .catch(() => { /* no App plugin: links simply open the app at home */ });
    return () => {
      removed = true;
      if (remove) void remove();
    };
  }

  // Capacitor's appStateChange cannot tell inactive from background, so only
  // "pause" (didEnterBackground) is used.
  onAppBackground(handler: () => void): () => void {
    return appLifecycleListener("pause", handler);
  }

  onAppResume(handler: () => void): () => void {
    return appLifecycleListener("resume", handler);
  }

  async hapticConfirm(): Promise<void> {
    try {
      await Haptics.notification({ type: NotificationType.Success });
    } catch { /* no Taptic Engine, or no plugin */ }
  }

  // R15.1. CFBundleShortVersionString + CFBundleVersion, read from the bundle.
  async getAppVersion(): Promise<{ version: string; build: string } | null> {
    try {
      const info = await App.getInfo();
      return info.version ? { version: info.version, build: info.build ?? "" } : null;
    } catch {
      return null;
    }
  }

  // R7.6 / task 3.8. iOS dims and locks a tablet mid-conversation otherwise,
  // which ends the session (backgrounding does, R3.6).
  async setKeepAwake(on: boolean): Promise<void> {
    try {
      await (on ? KeepAwake.keepAwake() : KeepAwake.allowSleep());
    } catch { /* plugin absent: the device just keeps its own timer */ }
  }

  // Task 14.8. The package's export is a `registerPlugin` proxy, so this works
  // without anything touching `Capacitor.Plugins`. A build without the native
  // plugin resolves quietly; the splash then never showed in the first place.
  hideSplash = async (options: { fadeOutDuration: number }): Promise<void> => {
    if (!Capacitor.isPluginAvailable("SplashScreen")) return;
    await SplashScreen.hide(options);
  };

  async getDeclaredAgeRange(): Promise<DeclaredAgeRange> {
    // R7.4 — the call site exists so the integration has somewhere to land;
    // the iOS 18+ Declared Age Range API needs a native plugin we cannot build
    // without a Mac. Returning `null` means "the platform did not say", which
    // callers MUST NOT read as "adult".
    return null;
  }
}

/** One per app: voice and project sound share the AVAudioSession. */
const nativeAudioSession = createAudioSessionArbiter({
  async voiceOn() {
    const { activateVoiceAudioSession } = await import("./audioSession");
    await activateVoiceAudioSession(Capacitor.isNativePlatform());
  },
  async voiceOff() {
    const { deactivateVoiceAudioSession } = await import("./audioSession");
    await deactivateVoiceAudioSession(Capacitor.isNativePlatform());
  },
  async playbackOn() {
    const { startSoundPlaybackSession } = await import("./audioSession");
    await startSoundPlaybackSession(Capacitor.isNativePlatform());
  },
  async playbackOff() {
    const { stopSoundPlaybackSession } = await import("./audioSession");
    await stopSoundPlaybackSession(Capacitor.isNativePlatform());
  },
});

/** A removable App "pause" / "resume" listener. Without the App plugin the
 *  handler never fires; the visibilitychange paths still run. */
function appLifecycleListener(event: "pause" | "resume", handler: () => void): () => void {
  let removed = false;
  let remove: (() => Promise<void>) | null = null;
  // One overload per event name, so a union argument does not type-check.
  (event === "pause" ? App.addListener("pause", handler) : App.addListener("resume", handler))
    .then((h) => {
      remove = h.remove;
      if (removed) void remove();
    })
    .catch(() => {});
  return () => {
    removed = true;
    if (remove) void remove();
  };
}

const LOCAL_DIR = "wbm-local";

/** One flat file per key; encoding keeps a `/` in a key from making directories. */
function localPath(key: string): string {
  return `${LOCAL_DIR}/${encodeURIComponent(key)}`;
}

/** Hosts whose links the app handles; must match the AASA file's domains. */
const APP_LINK_HOSTS = new Set(["worldby.me", "www.worldby.me", "dev.worldby.me"]);

/** `https://worldby.me/share/abc?x=1#y` → `/share/abc?x=1#y`; null for any other host. */
export function inAppPath(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.protocol !== "https:" || !APP_LINK_HOSTS.has(u.hostname)) return null;
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return null;
  }
}
